import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Plus, Trash2, Calendar, Activity, CheckCircle, Loader2, Clipboard } from 'lucide-react';
import { toast } from 'react-hot-toast';
import StableInput from './stable-input';
import GlobalPasteModal from './global-paste-modal';

const ProceduresForm = ({ data, onChange, onSave, isLoading, readonly = false, prechartingData, onGlobalPasteApply }) => {
  const [formData, setFormData] = useState([]);
  const [showTextModal, setShowTextModal] = useState(false);

  // Use ref to prevent unnecessary re-renders
  const formDataRef = useRef(formData);
  const updateTimeoutRef = useRef(null);
  formDataRef.current = formData;

  // Debounced update to prevent excessive re-renders
  const debouncedUpdate = useCallback((updatedData) => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    updateTimeoutRef.current = setTimeout(() => {
      if (onChange) {
        onChange(updatedData);
      }
    }, 100);
  }, [onChange]);

  useEffect(() => {
    if (data && Array.isArray(data)) {
      setFormData(data);
    } else if (data) {
      setFormData(data.procedures || []);
    }
  }, [data]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  const handleAddProcedure = useCallback(() => {
    const newProcedure = {
      id: Date.now(),
      date: '',
      name: '',
      indication: '',
      result: ''
    };
    
    setFormData(prev => {
      const updatedData = [...prev, newProcedure];
      debouncedUpdate(updatedData);
      return updatedData;
    });
  }, [debouncedUpdate]);

  const handleProcedureChange = useCallback((id, field, value) => {
    setFormData(prev => {
      const updatedData = prev.map(procedure => 
        procedure.id === id ? { ...procedure, [field]: value } : procedure
      );
      debouncedUpdate(updatedData);
      return updatedData;
    });
  }, [debouncedUpdate]);

  const handleDeleteProcedure = useCallback((id) => {
    setFormData(prev => {
      const updatedData = prev.filter(procedure => procedure.id !== id);
      debouncedUpdate(updatedData);
      return updatedData;
    });
  }, [debouncedUpdate]);

  const validateForm = () => {
    const errors = [];
    
    formData.forEach((procedure, index) => {
      if (procedure.date && !procedure.name) {
        errors.push(`Procedure ${index + 1}: Procedure name is required when date is provided`);
      }
      if (procedure.name && !procedure.date) {
        errors.push(`Procedure ${index + 1}: Date is required when procedure name is provided`);
      }
      if (procedure.indication && !procedure.name) {
        errors.push(`Procedure ${index + 1}: Procedure name is required when indication is provided`);
      }
      if (procedure.result && !procedure.name) {
        errors.push(`Procedure ${index + 1}: Procedure name is required when result is provided`);
      }
    });
    
    return errors;
  };

  const hasData = () => {
    return formData.length > 0 && formData.some(procedure => 
      procedure.name || procedure.date || procedure.indication || procedure.result
    );
  };

  const handleSave = async () => {
    if (!hasData()) {
      toast.error('Please add at least one procedure before saving');
      return;
    }

    const errors = validateForm();
    if (errors.length > 0) {
      toast.error(errors.join(', '));
      return;
    }
    
    if (onSave) {
      await onSave(formData);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-green-500" />
              Procedures
            </CardTitle>
            {!readonly && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowTextModal(true)}
                className="flex items-center gap-2"
              >
                <Clipboard className="h-4 w-4" />
                Extract from Text
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4"> 
          <div className="hidden xl:grid grid-cols-12 gap-4 font-medium text-sm text-gray-600 border-b pb-2">
            <div className="xl:col-span-1">Sl No</div>
            <div className="xl:col-span-2">Date (MM/DD/YY)</div>
            <div className="xl:col-span-3">Name of Procedure</div>
            <div className="xl:col-span-3">Indication</div>
            <div className="xl:col-span-2">Result</div>
            <div className="xl:col-span-1">Action</div>
          </div>
          {formData.map((procedure, index) => (
            <div key={procedure.id} className="grid grid-cols-1 gap-3 rounded-lg border border-gray-200 p-3 xl:grid-cols-12 xl:gap-4 xl:rounded-none xl:border-0 xl:p-0 items-start">
              <div data-label="Sl No" className="xl:col-span-1 text-sm text-gray-500 xl:pt-2 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 xl:before:hidden">
                {index + 1}
              </div>
              <div data-label="Date (MM/DD/YY)" className="xl:col-span-2 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 xl:before:hidden">
                <StableInput
                  placeholder="01/15/2024"
                  value={procedure.date}
                  onChange={(value) => handleProcedureChange(procedure.id, 'date', value)}
                  readonly={readonly}
                />
              </div>
              <div data-label="Name of Procedure" className="xl:col-span-3 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 xl:before:hidden">
                <StableInput
                  placeholder="Colonoscopy"
                  value={procedure.name}
                  onChange={(value) => handleProcedureChange(procedure.id, 'name', value)}
                  readonly={readonly}
                />
              </div>
              <div data-label="Indication" className="xl:col-span-3 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 xl:before:hidden">
                <Textarea
                  placeholder="Routine screening"
                  value={procedure.indication}
                  onChange={(e) => handleProcedureChange(procedure.id, 'indication', e.target.value)}
                  className="text-sm min-h-[40px] resize-none"
                  rows={2}
                  readOnly={readonly}
                />
              </div>
              <div data-label="Result" className="xl:col-span-2 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 xl:before:hidden">
                <Textarea
                  placeholder="Normal findings, no polyps detected"
                  value={procedure.result}
                  onChange={(e) => handleProcedureChange(procedure.id, 'result', e.target.value)}
                  className="text-sm min-h-[40px] resize-none"
                  rows={2}
                  readOnly={readonly}
                />
              </div>
              <div className="xl:col-span-1 xl:pt-2 flex justify-end xl:block">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteProcedure(procedure.id)}
                  disabled={readonly}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}

          <Button
            variant="outline"
            onClick={handleAddProcedure}
            disabled={readonly}
            className="w-full border-dashed border-2 border-gray-300 hover:border-gray-400"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add New Procedure
          </Button>
        </CardContent>
      </Card>
      {!readonly && onSave && (
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={isLoading || readonly || !hasData()}
            className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Save Procedures
              </>
            )}
          </Button>
        </div>
      )}
      <GlobalPasteModal
        isOpen={showTextModal}
        onClose={() => setShowTextModal(false)}
        sectionKey="procedures"
        prechartingData={prechartingData}
        onApply={onGlobalPasteApply}
        readonly={readonly}
      />
    </div>
  );
};

export default React.memo(ProceduresForm);
